import type {
    CreateVerificationRequest,
    EnhancedVerificationRequest,
} from '@common/types/treefort';
import type { LawyerType } from '@common/types/users';
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import CustomPhoneInput from '@/components/CustomPhoneInput';
import { invalidateAfterVerificationChange } from '@/queries/saidv';
import { updateIDVerification } from '@/services/TreefortService';
import useIdentityStore from '@/stores/useIdentityStore';
import { successSnackbar } from '@/utils/snackbar';
import IdvModal from './IdvModal';
import { useIDVClientForm } from './useIDVClientForm';

interface Props {
    selectedRow: EnhancedVerificationRequest;
    prospectId?: string;
    prospectVerId?: number;
    quoteApprover?: LawyerType | null;
    onClose: () => void;
}

export default function UpdateIDVModal({
    selectedRow,
    prospectId,
    prospectVerId,
    quoteApprover,
    onClose,
}: Props) {
    const queryClient = useQueryClient();
    const user = useIdentityStore((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const {
        clientUpdate,
        handleEmailChange,
        handleClientDataPhoneChange,
        handleClientDataCountryChange,
        toggleCheckbox,
    } = useIDVClientForm(selectedRow);

    const handleUpdateIDVInformation = async () => {
        if (clientUpdate.emailError || !selectedRow.clientRequestId) {
            return;
        }
        if (!prospectId || prospectVerId == null || !quoteApprover) {
            return;
        }

        const profiles = [
            {
                disableTreefortReviewEmails: true,
                requesterClientRequestId: selectedRow.requesterClientRequestId ?? '',
                clientRequestId: selectedRow.clientRequestId,
                email: clientUpdate.email,
                phoneNumber: clientUpdate.phone,
                countryCode: clientUpdate.countryCode || 'ca',
                dialCode: clientUpdate.dialCode || 1,
                isSecondaryIdRequired: clientUpdate.idVerification || false,
                isBankingVerificationRequired: clientUpdate.bankReview || false,
            },
        ];
        const payload: CreateVerificationRequest = {
            prospectVersionId: prospectVerId,
            prospectId,
            requesterReferenceId: quoteApprover.userId.toString(),
            initiatorName: quoteApprover.fullName,
            initiatorEmail: quoteApprover.email,
            initiatorOrganizationName: user?.lawFirm?.companyName ?? '',
            createNewClientRecord: true,
            fileReferenceId: selectedRow.fileReferenceId,
            profiles,
        };

        setIsLoading(true);
        try {
            await updateIDVerification(selectedRow.clientRequestId, payload);
            await invalidateAfterVerificationChange(queryClient, prospectId);
            successSnackbar('Information has been updated successfully!');
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <IdvModal
            onClose={onClose}
            actions={
                <>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={isLoading}
                        onClick={handleUpdateIDVInformation}
                    >
                        Confirm
                    </LoadingButton>
                </>
            }
        >
            <Typography variant="h2">Update IDV Information?</Typography>
            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                Please verify the information below to proceed with ID verification.
            </Typography>
            <Box sx={{ padding: '16px', paddingLeft: 0 }}>
                <Box
                    sx={{
                        marginBottom: 2,
                        padding: 2,
                        border: '1px solid #ddd',
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedRow.userName}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Email"
                            value={clientUpdate.email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            error={!!clientUpdate.emailError}
                            helperText={clientUpdate.emailError}
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <CustomPhoneInput
                            value={clientUpdate.phone}
                            selectedCountryCode={clientUpdate.countryCode || 'ca'}
                            onChange={(value) => handleClientDataPhoneChange(value)}
                            onCountryChange={(country) => handleClientDataCountryChange(country)}
                            error={!!clientUpdate.phoneError}
                            helperText={clientUpdate.phoneError}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={clientUpdate.idVerification}
                                    onChange={() => toggleCheckbox('idVerification')}
                                />
                            }
                            label="Request Second ID"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={clientUpdate.bankReview}
                                    onChange={() => toggleCheckbox('bankReview')}
                                />
                            }
                            label="Request Bank Review"
                        />
                    </Box>
                </Box>
            </Box>
        </IdvModal>
    );
}
